import { useState, useEffect } from "react";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config } from "../../Environment";
import { Spinner } from "../ui/spinner";
import {
  normalizeEncouragementMessages,
  pickRandomEncouragementMessage,
  substituteEncouragementPlaceholders,
} from "../../lib/dataEventEncouragement";

function isMeaningfulValue(v, depth = 0) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v === "boolean") return v === true;
  if (Array.isArray(v)) {
    if (v.length === 0) return false;
    if (depth >= 2) return true;
    return v.some((x) => isMeaningfulValue(x, depth + 1));
  }
  if (typeof v === "object") {
    const keys = Object.keys(v);
    if (keys.length === 0) return false;
    if (depth >= 2) return true;
    return keys.some((k) => isMeaningfulValue(v[k], depth + 1));
  }
  return true;
}

function isEmptyEventData(eventData) {
  if (!eventData || typeof eventData !== "object") return true;
  const keys = Object.keys(eventData);
  if (keys.length === 0) return true;
  return !keys.some((k) => isMeaningfulValue(eventData[k]));
}

/** Normalize legacy string content or partial objects. */
export function normalizeDataEventContent(content, fallbackKey) {
  if (typeof content === "string") {
    return {
      key: content,
      label: "",
      resetFieldKeys: [],
      trackedFieldKeys: [],
      encouragementMessages: [],
    };
  }
  if (!content || typeof content !== "object") {
    return {
      key: fallbackKey,
      label: "",
      resetFieldKeys: [],
      trackedFieldKeys: [],
      encouragementMessages: [],
    };
  }
  const encouragementMessages = normalizeEncouragementMessages(content);
  return {
    key: content.key ?? fallbackKey,
    label: content.label ?? "",
    resetFieldKeys: Array.isArray(content.resetFieldKeys)
      ? content.resetFieldKeys
      : [],
    trackedFieldKeys: Array.isArray(content.trackedFieldKeys)
      ? content.trackedFieldKeys
      : [],
    encouragementMessages,
  };
}

const DataEvent = ({
  processId,
  activity,
  userData = {},
  itemkey,
  content,
  onChange,
  onResetFieldKeys,
  onLogged,
}) => {
  const [isPosting, setIsPosting] = useState(false);
  const [isCounting, setIsCounting] = useState(false);
  const [encouragementLine, setEncouragementLine] = useState(null);
  const retryFetch = useRetryFetch();

  const normalized = normalizeDataEventContent(content, itemkey);
  const encouragementMessages = normalized.encouragementMessages;

  useEffect(() => {
    const run = async () => {
      const rawMessage = pickRandomEncouragementMessage(encouragementMessages);
      const showPreviewEncouragement = () => {
        if (!rawMessage) return;
        setEncouragementLine(
          substituteEncouragementPlaceholders(rawMessage, { count: 0 }),
        );
      };

      if (!processId) {
        showPreviewEncouragement();
        return;
      }

      try {
        const resetKeysSnapshot =
          Array.isArray(normalized.resetFieldKeys) &&
          normalized.resetFieldKeys.length > 0
            ? normalized.resetFieldKeys.reduce((acc, key) => {
                acc[key] = userData?.[key];
                return acc;
              }, {})
            : null;

        const eventDataToLog =
          Array.isArray(normalized.trackedFieldKeys) &&
          normalized.trackedFieldKeys.length > 0
            ? normalized.trackedFieldKeys.reduce((acc, key) => {
                if (Object.prototype.hasOwnProperty.call(userData, key)) {
                  acc[key] = userData[key];
                }
                return acc;
              }, {})
            : userData;
        const shouldLog = !isEmptyEventData(eventDataToLog);
        if (shouldLog) {
          setIsPosting(true);
          const response = await retryFetch(
            `${config.API_BASE}/api/process/${processId}/component/data-event`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                activity,
              dataEventKey: normalized.key,
                eventData: eventDataToLog,
              }),
            },
          );
          if (response.ok) {
            await response.json();
            onChange(normalized.key, 1);
            if (typeof onLogged === "function") {
              onLogged();
            }
            if (
              normalized.resetFieldKeys?.length > 0 &&
              typeof onResetFieldKeys === "function"
            ) {
              onResetFieldKeys(normalized.resetFieldKeys, resetKeysSnapshot);
            }
          }
        }
        if (rawMessage) {
          try {
            setIsCounting(true);
            const q = new URLSearchParams({ processId });
            if (activity) q.set("activity", activity);
            const searchRes = await retryFetch(
              `${config.API_BASE}/api/processDataEvent/search?${q.toString()}`,
              { credentials: "include" },
            );
            let count = 0;
            if (searchRes.ok) {
              const data = await searchRes.json();
              count = Array.isArray(data.list) ? data.list.length : 0;
            }
            setEncouragementLine(
              substituteEncouragementPlaceholders(rawMessage, { count }),
            );
          } catch (encErr) {
            console.warn("Data event encouragement count failed:", encErr);
          } finally {
            setIsCounting(false);
          }
        }
      } catch (err) {
        console.log(`Data event failed: ${err.message}`);
      } finally {
        setIsPosting(false);
      }
    };
    run();
    // Intentionally once on mount: capture one snapshot when the pane is entered (same as before).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- do not re-post on userData/activity changes
  }, []);

  const isLoading = isPosting || isCounting;

  return (
    <div className="mt-5 text-start space-y-3">
      {isLoading ? (
        <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner size="sm" />
            <span>Saving and preparing your insight...</span>
          </div>
          <div className="h-3 w-4/5 rounded bg-muted animate-pulse" />
        </div>
      ) : encouragementLine ? (
        <p
          className="text-sm text-muted-foreground border-l-4 border-primary/50 pl-3 py-1 rounded-sm bg-muted/20"
          role="status"
        >
          {encouragementLine}
        </p>
      ) : null}
    </div>
  );
};

export default DataEvent;
